/**
 * Grid cell renderer for a user entity-reference column.
 *
 * Renders the user's display name as an outlined chip. Staff viewers get a
 * clickable chip that navigates to /support/users/:id; non-staff or system
 * commands (id 0 / null) get a plain chip. A null user reference renders
 * nothing.
 *
 * Intended for columns whose value is an odl-app user entity-ref object
 * { type, id, first_name, last_name, name, email }.
 *
 * Contains:
 * - resolveUserName — display-name helper (not exported)
 * - UserCell        — the React component (default export)
 */

import { Chip } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { useUser } from '@sophocles/app';

/**
 * Derives the display name from a user entity-ref object.
 *
 * @param {{ name?: string, first_name?: string, last_name?: string, email?: string } | null} u
 * @param {string} fallback - i18n-resolved "Unknown" string
 * @returns {string}
 */
function resolveUserName(u, fallback) {
  if (!u) return fallback;
  if (u.name) return u.name;
  const full = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim();
  if (full) return full;
  if (u.email) return u.email;
  return fallback;
}

/**
 * Renders a user entity-reference as a small outlined chip.
 * Staff viewers get a router-link to /support/users/:id when id is truthy.
 *
 * @param {{ user: { type?: string, id?: number|string, first_name?: string, last_name?: string, name?: string, email?: string } | null }} props
 */
export default function UserCell({ user }) {
  const { t } = useTranslation();
  const { isStaff } = useUser();

  if (!user) return null;

  const name = resolveUserName(user, t('general.unknown', 'Unknown'));

  if (isStaff && user.id) {
    return (
      <Chip
        size="small"
        label={name}
        variant="outlined"
        clickable
        component={RouterLink}
        to={`/support/users/${user.id}`}
        onClick={e => e.stopPropagation()}
        // Rendered as an <a>, so the UA a:visited rule would otherwise recolor
        // the chip once the profile has been opened. Pin it to the chip's color.
        sx={{ '&:visited': { color: 'text.primary' } }}
      />
    );
  }

  return <Chip size="small" label={name} variant="outlined" />;
}

UserCell.propTypes = {
  /** odl-app user entity-reference object, or null for system/anonymous commands. */
  user: PropTypes.shape({
    type: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
  }),
};
